import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Clock, Edit } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useCreateNscResponseTimeConfiguration,
  useGetNscResponseTimeConfiguration,
  useUpdateNscResponseTimeConfiguration,
} from "@features/cx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";

export interface TimeConfigData {
  acknowledgmentTime: string;
  responseTime: string;
  resolutionTime: string;
}

export const TimeConfigurationCard = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: getNscResponseTimeConfiguration } =
    useGetNscResponseTimeConfiguration();
  const { mutateAsync: updateNscResponseTimeConfiguration } =
    useUpdateNscResponseTimeConfiguration();
  const { mutateAsync: createNscResponseTimeConfiguration } =
    useCreateNscResponseTimeConfiguration();

  // Find the response time configuration using config_type
  const responseTimeConfig = getNscResponseTimeConfiguration?.find(
    (config) => config.configType === "RESPONSE_TIME"
  );

  const [isEditable, setIsEditable] = useState(
    !responseTimeConfig?.configurationData // editable by default if no config
  );
  const [formData, setFormData] = useState<TimeConfigData>({
    acknowledgmentTime: "",
    responseTime: "",
    resolutionTime: "",
  });

  const formFields: FormField[] = [
    {
      name: "acknowledgmentTime",
      type: "number",
      label: "Acknowledgment Time (hours)",
      placeholder: "24",
      min: 1,
      disabled: !isEditable,
      required: true,
      helperText: "Time within which requests must be acknowledged",
      className: "col-span-1",
      classes: {
        input: `${
          isEditable
            ? "w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            : "cursor-not-allowed bg-gray-100 w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        }`,
      },
    },
    {
      name: "responseTime",
      type: "number",
      label: "Response Time (hours)",
      placeholder: "48",
      min: 1,
      disabled: !isEditable,
      required: true,
      helperText: "Time within which initial response must be provided",
      className: "col-span-1",
      classes: {
        input: `${
          isEditable
            ? "w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            : "cursor-not-allowed bg-gray-100 w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        }`,
      },
    },
    {
      name: "resolutionTime",
      type: "number",
      label: "Resolution Time (hours)",
      placeholder: "168",
      min: 1,
      disabled: !isEditable,
      required: true,
      helperText: "Maximum time allowed for complete resolution",
      className: "col-span-1",
      classes: {
        input: `${
          isEditable
            ? "w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            : "cursor-not-allowed bg-gray-100 w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        }`,
      },
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    if (responseTimeConfig?.configurationData) {
      const config = responseTimeConfig.configurationData;
      const updatedData: TimeConfigData = {
        acknowledgmentTime: config.acknowledgeTime,
        responseTime: config.responseTime,
        resolutionTime: config.resolutionTime,
      };

      setFormData(updatedData);
      form.reset(updatedData);
      setIsEditable(false); // disable fields once data is fetched
    } else {
      setIsEditable(true); // editable if no data
    }
  }, [responseTimeConfig, form]);

  const handleFieldChange = (fieldName: string, value: any): void => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleFormSubmit = (data: TimeConfigData): void => {

    const acknowledgmentTime = Number(data.acknowledgmentTime);
    const responseTime = Number(data.responseTime);
    const resolutionTime = Number(data.resolutionTime);

    if (acknowledgmentTime >= responseTime || responseTime >= resolutionTime) {
      toast({
        title: "Error",
        description: "Acknowledgment time should be less than response time and response time should be less than resolution time",
        variant: "destructive",
      });
      return;
    }
    const operation = responseTimeConfig?.configurationData ? "UPDATE" : "ADD";
    
    switch (operation) {
      case "UPDATE":
        const payloadEdit = {
          config_type: "RESPONSE_TIME",
          remote_utility_id: remoteUtilityId,
          configuration_data: {
            response_time: Number(data.responseTime),
            acknowledge_time: Number(data.acknowledgmentTime),
            resolution_time: Number(data.resolutionTime),
          },
        };
        updateNscResponseTimeConfiguration(payloadEdit);
        break;
      case "ADD":
        const payloadAdd = {
          config_type: "RESPONSE_TIME",
          remote_utility_id: remoteUtilityId,
          configuration_data: {
            response_time: Number(data.responseTime),
            acknowledge_time: Number(data.acknowledgmentTime),
            resolution_time: Number(data.resolutionTime),
          },
        };
        createNscResponseTimeConfiguration(payloadAdd);
        break;
    }
  };

  const handleFormReset = (): void => {
    form.reset();
    setFormData({
      acknowledgmentTime: "",
      responseTime: "",
      resolutionTime: "",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="card-heading-text flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Response Time Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="mt-4">
        {!isEditable && (
          <div className="flex justify-end">
            <button
              onClick={() => setIsEditable(true)}
              className="border border-input text-sm px-3 py-1.5 bg-white text-black rounded hover:bg-gray-50 flex items-center gap-1"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
          </div>
        )}
        <DynamicForm
          fields={formFields}
          form={form}
          onFieldChange={handleFieldChange}
          onReset={handleFormReset}
          onSubmit={handleFormSubmit}
          gridColumns={3}
          responsiveColumns={{
            sm: 1,
            md: 3,
          }}
          config={{
            showResetButton: isEditable,
            showSubmitButton: isEditable,
            resetButtonText: "Cancel",
            submitButtonText: "Save",
            spacing: "comfortable",
            classes: {
              form: "space-y-0",
              container: "space-y-2",
              label: "text-sm font-medium",
              error: "text-xs text-red-600 mt-1",
              input:
                "w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              helperText: "text-xs text-muted-foreground mt-1",
              buttonContainer: "flex justify-end gap-2",
            },
          }}
        />
      </CardContent>
    </Card>
  );
};